import { ref, watch, onMounted, onUnmounted } from "vue";
import { errorMessage } from "@/composables/use-admin-list";

export const usePagedSearch = (
  fetchPage,
  context,
  defaultSort = "createdAt:desc"
) => {
  const search = ref("");
  const appliedSearch = ref("");
  const sort = ref(defaultSort);
  const page = ref(1);
  const data = ref([]);
  const total = ref(0);
  const totalPages = ref(0);
  const loading = ref(false);
  const error = ref("");
  let revision = 0;
  let disposed = false;
  let debounce;
  const refresh = async () => {
    const request = ++revision;
    loading.value = true;
    error.value = "";
    try {
      const response = await fetchPage({
        search: appliedSearch.value,
        sortBy: sort.value,
        page: page.value,
        limit: 100
      });
      if (disposed || request !== revision) return;
      if (!response || !Array.isArray(response.results))
        throw new Error("Unable to load participants. Please try again.");
      total.value = response.totalResults;
      totalPages.value = response.totalPages;
      if (page.value > Math.max(1, totalPages.value)) {
        page.value = Math.max(1, totalPages.value);
        return refresh();
      }
      data.value = response.results;
    } catch (err) {
      if (disposed || request !== revision) return;
      error.value = errorMessage(err);
      data.value = [];
    } finally {
      if (!disposed && request === revision) loading.value = false;
    }
  };
  const reset = () => {
    clearTimeout(debounce);
    appliedSearch.value = search.value.trim();
    page.value = 1;
    data.value = [];
    refresh();
  };
  watch(search, () => {
    clearTimeout(debounce);
    revision++;
    data.value = [];
    loading.value = true;
    debounce = setTimeout(reset, 300);
  });
  watch(context, reset);
  const changePage = next => {
    if (loading.value) return;
    page.value = next;
    refresh();
  };
  const changeSort = ({ key, direction }) => {
    sort.value = `${key}:${direction}`;
    reset();
  };
  onMounted(refresh);
  onUnmounted(() => {
    disposed = true;
    revision++;
    clearTimeout(debounce);
  });
  return {
    search,
    sort,
    page,
    data,
    total,
    totalPages,
    loading,
    error,
    refresh,
    changePage,
    changeSort
  };
};
